import React, { useRef, useState, useEffect } from 'react';
import { FaMinus } from 'react-icons/fa';
import SelectProductVeriation from './SelectProductVeriation';
import { uploadToS3 } from '../../../config/aws';
import { USER_NAME } from '../../../constant';
import { sendToBackend } from '../../../utils/utilities';
import { showSuccessAlert, showErrorAlert, showWarningAlert, showInfoAlert, showConfirmDialog } from '../../../utils/sweetAlertConfig';
import '../../../styles/sweetAlert.css';
import { checkAvailableCredits, decreaseCredits } from '../../../dbApiFunctions';

const CreateNewProduct = ({ onRequestClose, fetchProducts, setMyProducts, setCreditsAvailable, setProductPrompt, setIsProductModalOpen, handleProductSelect, setRefNum }) => {
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showImageVariation, setShowImageVariation] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [productName, setProductName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [inputError, setInputError] = useState('');

  const [rembgWaitTime, setRembgWaitTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const categories = {
    'Categories': {
      'Seating': ['Sofa', 'Loveseat', 'Armchair', 'Recliner', 'Sectional Sofa', 'Chase Lounge'],
      'Living Room': ['Sofas', 'Accent Chairs', 'Coffee Tables', 'TV Stands', 'Side Tables'],
      'Bedroom': ['Bed Frames', 'Nightstands', 'Dressers', 'Wardrobes', 'Benches'],
      'Dining Room': ['Dining Tables', 'Dining Chairs', 'Bar Stools', 'Buffets & Sideboards', 'China Cabinets'],
      'Office': ['Desks', 'Office Chairs', 'Bookcases', 'File Cabinets', 'Storage Shelves'],
      'Outdoor': ['Patio Sofas', 'Outdoor Dining Tables', 'Lounge Chairs', 'Outdoor Benches', 'Side Tables'],
      'Other': ['Other']
    }
  };

  // Load images from sessionStorage on component mount
  // useEffect(() => {
  //   const storedImages = sessionStorage.getItem('uploadedImages');
  //   if (storedImages) {
  //     setUploadedImages(JSON.parse(storedImages));
  //   }
  // }, []);

  // // Save images to sessionStorage whenever uploadedImages changes
  // useEffect(() => {
  //   sessionStorage.setItem('uploadedImages', JSON.stringify(uploadedImages));
  // }, [uploadedImages]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDragOver = (event) => {
    event.preventDefault();
    event.currentTarget.classList.add('bg-gray-200');
  };

  const handleDragLeave = (event) => {
    event.currentTarget.classList.remove('bg-gray-200');
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.currentTarget.classList.remove('bg-gray-200');
    const files = event.dataTransfer.files;
    handleFiles(files);
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleChange = (event) => {
    const files = event.target.files;
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const imageFiles = Array.from(files).filter(file => {
      if (!file.type.startsWith('image/')) {
        showErrorAlert('Only image files are allowed. Please select a valid image file.', 'Invalid File Type');
        return false;
      }
      return true;
    });

    if (imageFiles.length === 0) {
      return;
    }

    // Check if adding these files would exceed the 4 image limit
    const totalImagesAfterUpload = uploadedImages.length + imageFiles.length;
    if (totalImagesAfterUpload > 4) {
      showErrorAlert(`You can only upload a maximum of 4 images. You currently have ${uploadedImages.length} images and trying to add ${imageFiles.length} more.`, 'Maximum Images Exceeded');
      return;
    }

    const newImages = imageFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setUploadedImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setUploadedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setShowCategoryDropdown(false);

  };

  const handleImageSelect = (image) => {
    if (selectedImageIndex !== null) {
      setUploadedImages(prev => {
        const newImages = [...prev];
        newImages[selectedImageIndex] = {
          file: null,
          preview: image.src
        };
        return newImages;
      });
    }
    setShowImageVariation(false);
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setShowImageVariation(true);
  };

  const handleUpload = async () => {
    // console.log('Uploading... clicked');

    if (!productName) {
      showErrorAlert('Please enter a product name', 'Missing Product Name');
      return;
    }

    if (uploadedImages.length === 0) {
      showErrorAlert('Please upload at least one image', 'No Images Selected');
      return;
    }

    setIsUploading(true);
    const email = sessionStorage.getItem('email');
    const password = sessionStorage.getItem('password');
    const response = await checkAvailableCredits(email, password, 10);
    if (!response.status) {
      showErrorAlert(response.message, 'Insufficient Credits');
      return;
    }

    let timer = null;
    try {
      for (const image of uploadedImages) {
        const file = image.file;
        if (!file) {
          console.error('Invalid file object');
          continue;
        }
        // Generate a 3-digit number for the filename (001, 002, etc.)
        const index = uploadedImages.indexOf(image);
        const threeDigitNumber = String(index).padStart(3, '0');
        const fileExtension = file.name.split('.').pop();
        const newFileName = `${threeDigitNumber}.${fileExtension}`;

        const s3Path = `USERS/${USER_NAME}/all_products/${productName}/user_product_images/w_bg/${newFileName}`;
        await uploadToS3(s3Path, file.type, file);
        // console.log("createNewProduct = Uploaded image to S3 at location ", `USERS/${USER_NAME}/all_products/${productName}/user_product_images/w_bg/${newFileName}`);
      }
      // console.log('Images uploaded successfully!');

      //save Category to s3 
      const s3Path = `USERS/${USER_NAME}/all_products/${productName}/metadata.json`;
      const metadata = {
        category: selectedCategory,
        productName: productName,
        images: uploadedImages.map(image => image.name)
      };
      await uploadToS3(s3Path, 'application/json', JSON.stringify(metadata));
      // console.log("createNewProduct = Uploaded metadata to S3 at location ", `USERS/${USER_NAME}/all_products/${productName}/metadata.json`);

      setRembgWaitTime(uploadedImages.length * 20000);
      const response = await sendToBackend('rembg', {
        productName: productName,
        category: selectedCategory,
        userName: USER_NAME
      }, 10, setCreditsAvailable);

      if (response && response.hasOwnProperty('est_time')) {
        setRembgWaitTime(response.est_time);
        setIsProcessing(true);
        setProgress(0);

        // Start progress timer
        const intervalTime = 100; // Update every 100ms for smoother progress
        const steps = response.est_time * 10; // Total steps (10 per second)
        let currentStep = 0;

        timer = setInterval(() => {
          currentStep++;
          const newProgress = (currentStep / steps) * 100;
          setProgress(newProgress > 100 ? 100 : newProgress);

          if (currentStep >= steps) {
            clearInterval(timer);
            setIsProcessing(false);
            setIsUploading(false);
            onRequestClose();

            showSuccessAlert('Product Created Successfully!', 'Your product has been created and is being processed.', 'You can now view your product in the products list');
          }
        }, intervalTime);
      } else {
        setIsUploading(false);
        onRequestClose();
        showSuccessAlert('Product Created!', 'Your product has been created successfully.', 'You can now view your product in the products list');
      }
      fetchProducts(USER_NAME, setMyProducts);
      // setUploadedImages([]);
      // setProductName('');

    } catch (error) {
      console.error('Error uploading images:', error);
      // alert('Error uploading images. Please try again.');

      showErrorAlert('Error uploading images. Please try again.', 'Upload Failed');
      setIsProcessing(false);
      setIsUploading(false);
      if (timer) {
        clearInterval(timer);
      }
    }
  };

  const handleCategorySearchChange = (e) => {
    setCategorySearch(e.target.value);
  };

  const filterCategories = (categories, searchTerm) => {
    if (!searchTerm) return categories;

    const filteredCategories = {};

    Object.entries(categories).forEach(([mainCategory, subCategories]) => {
      const filteredSubCategories = {};

      Object.entries(subCategories).forEach(([subCategory, items]) => {
        const filteredItems = items.filter(item =>
          item.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (filteredItems.length > 0) {
          filteredSubCategories[subCategory] = filteredItems;
        }
      });

      if (Object.keys(filteredSubCategories).length > 0) {
        filteredCategories[mainCategory] = filteredSubCategories;
      }
    });

    return filteredCategories;
  };

  const handleProductNameChange = (e) => {
    const value = e.target.value;
    // Allow alphanumeric characters and underscores
    if (value === '' || /^[a-zA-Z0-9_]+$/.test(value)) {
      setProductName(value);
      setInputError('');
    } else {
      setInputError('Only letters, numbers, and underscores are allowed');
    }
  };

  return (
    <div className="w-full">
      {showImageVariation ? (
        <SelectProductVeriation
          onBack={() => setShowImageVariation(false)}
          onImageSelect={handleImageSelect}
          selectedProduct={productName}
          setProductPrompt={setProductPrompt}

          setIsProductModalOpen={setIsProductModalOpen}
          handleProductSelect={handleProductSelect}
          setRefNum={setRefNum}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ">
          <div>
            <h2 className="text-lg font-semibold mb-4">Image Guidelines</h2>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center text-green-600">
                <i className="fas fa-check-circle mr-2"></i>
                Use a good smartphone in bright, even lighting with a clean lens-avoid glare or shadows.
              </li>
              <li className="flex items-center text-green-600">
                <i className="fas fa-check-circle mr-2"></i>
                Make sure the full product is in sharp focus and the only item in the frame.
              </li>
              <li className="flex items-center text-green-600">
                <i className="fas fa-check-circle mr-2"></i>
                Take 3-5 clear photos from multiple angles (front, side, 45°, back, top), showing the entire piece.
              </li>
              <li className="flex items-center text-red-600">
                <i className="fas fa-times-circle mr-2"></i>
                Don't mix products or variants-train each one separately, and avoid cropped or zoomed-in shots.
              </li>
            </ul>

            <div className="mb-4">
              <label htmlFor="style-name" className="block text-gray-700 mb-2">Name Your Product</label>
              <input
                type="text"
                id="style-name"
                placeholder="Product Name"
                className={`w-full border ${inputError ? 'border-red-500' : 'border-gray-300'} rounded-lg p-2`}
                value={productName}
                onChange={handleProductNameChange}
              />
              {inputError && (
                <p className="text-red-500 text-sm mt-1">{inputError}</p>
              )}
            </div>
            <button
              className="bg-purple-600 text-white rounded-lg px-4 py-2 w-full hover:bg-purple-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
              onClick={handleUpload}
              disabled={isUploading}
              aria-disabled={isUploading}
              style={{ pointerEvents: isUploading ? 'none' : 'auto' }}
            >
              {isProcessing ? (
                <>
                  <div className="absolute left-0 top-0 h-full bg-purple-800 transition-all duration-100" style={{ width: `${progress}%` }}></div>
                  <span className="relative z-10">Processing... {Math.round(progress)}%</span>
                </>
              ) : (
                isUploading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Creating...</span>
                  </div>
                ) : 'Create'
              )}
            </button>
          </div>

          <div>
            {uploadedImages.length === 0 ? (
              // Original large upload box when no images
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center p-8 bg-gray-50 file-upload h-full"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <i className="fas fa-cloud-upload-alt text-purple-600 text-4xl mb-4"></i>
                <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                <p className="text-gray-400 mb-4">Max. File Size: 30MB</p>
                <button
                  type="button"
                  className="bg-purple-600 text-white rounded-lg px-4 py-2 hover:bg-purple-700 transition-colors duration-200"
                  onClick={handleClick}
                >
                  Browse File
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleChange}
                />
              </div>
            ) : (
              // Grid layout when images are uploaded
              <div className="grid grid-cols-3 gap-4">
                {/* Upload Box always first in grid */}
                <div
                  className="h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center bg-gray-50 cursor-pointer hover:bg-gray-100 transition-all duration-200"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleClick}
                >
                  <i className="fas fa-cloud-upload-alt text-purple-600 text-xl"></i>
                  <p className="text-gray-600 text-xs text-center mt-1">Upload Image</p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleChange}
                  />
                </div>
                {/* Uploaded images after the upload box */}
                {uploadedImages.map((image, index) => (
                  <div
                    key={index}
                    className="relative group"
                  >
                    <img
                      src={image.preview}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-24 object-contain rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 rounded-lg flex items-center justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage(index);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600"
                      >
                        <FaMinus size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateNewProduct;
